<template>
  <div class="panel">
    <div v-if="!isImporting" class="layout">
      <div class="content">
        Upload a spreadsheet with one team member per row.
        Existing members will be uploaded.
        <div class="links">
          <a href="/static/downloads/Brandly-Sample-Import.xlsx" download="Brandly-Sample-Import.xlsx">Download a sample file ></a>
        </div>

        <p class="fineprint">NOTE: Invites will not be sent.</p>
      </div>
      <div class="buttons">
        <b-button variant="default" @click="initImporter" :disabled="isImporterBusy">
          <WaveSpinner v-if="isImporterBusy" color="white" inline />
          <span v-else>Start</span>
        </b-button>
        <b-button v-if="!isImporterBusy" @click="$emit('cancel')" variant="primary">
          Cancel
        </b-button>
      </div>
    </div>

    <div v-else class="layout"> <!-- Will be shown when data from importer will be received -->
      <div class="content">
        <b-progress :max="importerData.length" height="40px">
          <b-progress-bar :value="savedUsersQty" variant="success" class="custom-progressbar">
            Saved: {{ savedUsersQty }} / {{ importerData.length }}
          </b-progress-bar>
          <b-progress-bar :value="notSavedUsersQty" variant="danger" class="custom-progressbar">
            Unsaved: {{ notSavedUsersQty }} / {{ importerData.length }}
          </b-progress-bar>
        </b-progress>
        <b-alert show variant="danger" v-if="cancelled">
          Operation cancelled
        </b-alert>
        <b-alert show variant="danger" v-for="(userData, index) in invalidData" :key="index">
          <div>Data: {{ userData.first_name }} {{ userData.last_name }} ({{ userData.email }})</div>
          <div v-for="(errorMessages, fieldName) in userData.errors">
            <b>{{ fieldName }}: {{ errorMessages[0] }}</b>
          </div>
        </b-alert>
      </div>
      <div class="buttons">
        <b-button v-if="isImportingDone" variant="default" @click="$emit('cancel')">
          Done
        </b-button>
        <b-button v-else variant="primary" @click="cancelImporting" >
          Cancel
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from "vuex";
  import { postTeamUser } from "@/services/dashboard";
  import splitter from 'full-name-splitter';

  // Flatfile importer settings
  const licenseKey = "8023fb60-98f3-11e9-9a01-23081a1ddf80";
  const scriptSource = "https://unpkg.com/flatfile-csv-importer/build/dist/index.min.js";
  const emailValidator = {
    validate: 'regex_matches',
    // From http://emailregex.com/
    regex: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    error: 'Please provide a valid email'
  }
  const addressValidator = {
    validate: 'regex_matches',
    regex: /SET LATER/i,
    error: 'Address not found'
  }
  const dataConfig = {
    fields: [
      { label: "First Name", key: "first_name", isRequired: false },
      { label: "Last Name", key: "last_name", isRequired: false },
      { label: "Name", key: "full_name", isRequired: false },
      { label: "Email", key: "email", isRequired: true, validator: emailValidator },
      { label: "Title", key: "title", isRequired: false },
      { label: "Phone 1", key: "ph1", isRequired: false },
      { label: "Phone 2", key: "ph2", isRequired: false },
      { label: "Address", key: "address", isRequired: false, validator: addressValidator },
    ],
    type: 'Team member',
    allowCustom: false,
    managed: true,   // Enable XLS upload
    styleOverrides: {
      borderRadius: '0px',                        // Flatfile wrapper, modals, & buttons
      primaryButtonColor: '#ea1947',              // Primary buttons
      linkColor: '#ea1947',                       // Links
      linkAltColor: '#4a90e2',                    // :hover, :active, & :focus states of links
      primaryTextColor: '#50535b',                // Main text color, background-color of certain buttons
      secondaryTextColor: '#9daab6',              // 'Dimmed' text
      errorColor: '#d0011b',                      // Invalid and error indicator borders, icons, and text
      successColor: '#60b700',                    // Success text, buttons, icons, toggles, required indicators
      warningColor: '#f6a623',                    // Warning text and icons
      borderColor: '#e8e8e8',                     // Flatfile wrapper, modals, and some buttons
      fontFamily: '"aktiv-grotesk", sans-serif',  // Main font
    },
    maxRecords: 1000,
  }
  let importer;

  export default {
    data() {
      return {
        inviteEmail: '',
        isImporterBusy: false,    // To indicate that importer is running
        isImporting: false,       // To indicate that we are in the "importing" stage
        isImportingDone: false,   // To indicate that the importing is done
        importerData: [],         // Data received from importer
        savedUsersQty: 0,
        notSavedUsersQty: 0,
        invalidData: [],          // To keep invalid data (after the API request)
        cancelled: false
      }
    },
    computed: {
      ...mapGetters(["teamId", "teamAddresses"]),
    },
    async mounted() {
      await this.loadTeamAddresses();
      this.buildAddressIndex();
    },
    methods: {
      ...mapActions(["reloadTeamInfo", "loadTeamAddresses", "reloadTeamMembers"]),
      async initImporter() {
        this.isImporterBusy = true;
        if (document.querySelector("script[src='" + scriptSource + "']")){
          // Script tag already added - re-create importer (in case it was lost eg after a hot reload) and open
          await this.createImporter();
        } else {
          // Script tag does not added - we will add it and when it will be loaded,
          // we will create importer (it will open automatically).
          let script = document.createElement("script");
          script.setAttribute("src", scriptSource);
          script.setAttribute("type", "text/javascript");
          script.onload = () => this.createImporter();
          document.head.appendChild(script);
        }
      },
      async createImporter() {
        importer = new FlatfileImporter(licenseKey, dataConfig);
        await this.openImporter();
      },
      async openImporter() {
        importer.requestDataFromUser().then(results => {
          importer.close();
          this.saveUsers(results.data)
        }, () => {
          // Importer is closed by a user.
          this.isImporterBusy = false;
        })
      },
      async saveUsers(data) {
        this.isImporting = true;
        this.isImportDone = false;
        this.invalidData = [];
        this.importerData = data;
        this.savedUsersQty = 0;
        this.notSavedUsersQty = 0;
        for (const userData of data) {
          if (this.cancelled) break;
          for (let key in userData) {
            userData[key] = userData[key].trim();
          }
          const { full_name, first_name, last_name, email, title, ph1, ph2, address } = userData;
          const personal_info = { title, ph1, ph2 };
          const default_team_address = address ? this.findTeamAddress(address) : null;
          if (full_name) {
            [first_name, last_name] = splitter(full_name);
          }
          const payload = { first_name, last_name, email, personal_info, default_team_address };
          try {
            await postTeamUser(this.teamId, payload);
            this.savedUsersQty += 1;
          } catch(e) {
            this.notSavedUsersQty += 1;
            if (e.response && e.response.data) {
              // Expect an object with an errors key. Use generic message if there is no errors key.
              let {errors=["Error saving user"]} = e.response.data;
              this.invalidData.push({...userData, errors})
            }
          }
        }
        if (this.savedUsersQty > 0) {
          this.reloadTeamInfo();
          this.reloadTeamMembers();
        }
        this.isImportingDone = true;
      },
      buildAddressIndex() {
        let seen = {state: {}, postcode: {}, line1: {}, line2: {}, line3: {}, city: {}, country: {}};
        this.addressIndex = {};
        for (let addr of this.teamAddresses) {
          for (let key in seen) {
            let value = addr[key];
            if (!value) continue;
            // Remove non-alphanumerics from key.
            value = value.toLowerCase();
            if (seen[key][value]) {
              delete this.addressIndex[value];
            } else {
              seen[key][value] = true;
              this.addressIndex[value] = addr.id;
            }
          }
        }

        // Update the validation regex to match only any of the values
        let keysEscaped = Object.keys(this.addressIndex).map(s => s.replace(/[^a-z0-9 ]/g, '\\$&'));
        addressValidator.regex = new RegExp('^('+keysEscaped.join('|')+')$', 'i');
        console.log(addressValidator.regex);
      },
      findTeamAddress(matchKey) {
        // Given a string, find the matching team address id.
        // We allow matching by any field, as long as
        // that field is unique among the addresses.
        // So if matchKey is "CA" and there is one
        // address with state = "CA", its good.
        return this.addressIndex[matchKey.toLowerCase()];
      },
      cancelImporting() {
        this.cancelled = true;
      }
    },
  }
</script>

<style scoped lang="scss">
  @import '~scss/colors';

  a.back {
    color: $red;
    font-size: small;
    font-weight: bold;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .custom-progressbar {
    height: 40px;
    line-height: 40px;
    font-size: 14px;
  }

  .panel {
    border: 1px solid $gray-1;
    background: $gray-3;
  }

  .layout {
    display: flex;
    flex-flow: row nowrap;
  }

  .content {
    padding: 0.5em;
    min-width: 300px;
    flex-grow: 1;

    > div {
      margin: 0.5em;
    }
  }

  .links {
    a { display: block; color: $red; }
    padding: 0.5em 0;
  }

  .fineprint {
    font-size: smaller;
    color: $gray-0;
  }

  .buttons {
    padding: 1em;
    white-space: nowrap;
  }

  @media screen and (max-width: 575.98px) {
    .panel {
      border-right: none;
      border-left: none;
      margin: 0 -1em;
    }

    .layout {
      flex-flow: column nowrap;
    }

    .buttons {
      padding-top: 0;
      text-align: right;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/dashboard/team/TeamMembersImport.vue